import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncComponentHookResult } from '../../Types/ComponentHookResult';
import { usePerformanceDetailsQuery } from '../api/usePerformanceDetailsQuery';
import {
  formatPercentage as formatNumberPercentage,
  formatCurrency as formatNumberCurrency,
} from '../../CommonUtils/formatNumber';
import { PerformancePeriodDTO, TabSlug } from '../types';
import {
  setGroupSelectionTabsList,
  setGroupSelectionDefaultTabIndex,
  setGroupSelectionLoadingStatus,
} from '../state/actions';
import { selectGroupSelectionData } from '../state/selectors';

const formatCurrency = value =>
  formatNumberCurrency(value, { decimalPlaces: 2, thousandSeparated: true });

const formatPercentage = value => formatNumberPercentage(value, { decimalPlaces: 2 });

export interface Cell {
  text: string;
}

export type Row = {
  id?: string;
  cells: Cell[];
  isBenchmark?: boolean;
  isTotal?: boolean;
  childRows?: Row[];
  color?: string | null;
};

export interface TableData {
  headers: Cell[];
  rows: Row[];
}

export interface PerformanceDetailsData {
  tabList: string[];
  defaultTabIndex: number;
  tableData: TableData | null;
}

interface UsePerformanceDetailsParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate: string;
  endDate: string;
  accountId: string;
  groupId: string;
  isEmbeddedMobile: boolean;
}

export const usePerformanceDetails = ({
  clientId,
  period,
  startDate,
  endDate,
  accountId,
  groupId,
  isEmbeddedMobile,
}: UsePerformanceDetailsParams): AsyncComponentHookResult<PerformanceDetailsData> => {
  const dispatch = useDispatch();

  const {
    tabList: stateTabList,
    defaultTabIndex: stateDefaultTabIndex,
    selectedTabIndex: stateSelectedTabIndex,
  } = useSelector(selectGroupSelectionData(TabSlug.Performance));

  const queryInfo = usePerformanceDetailsQuery({
    clientId,
    period,
    startDate,
    endDate,
    accountId,
    groupId,
  });

  useEffect(() => {
    dispatch(setGroupSelectionLoadingStatus(queryInfo.isLoading, TabSlug.Performance));
  }, [dispatch, queryInfo.isLoading]);

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const { periodLabels, groups, totals, benchmarks, defaultGroup } = queryInfo.data;

    // Empty state
    if (groups.length === 0) {
      return {
        tabList: [],
        tableData: null,
        defaultTabIndex: 0,
      };
    }

    const tableData = {} as TableData;

    const tabList = stateTabList ?? groups.map(({ name }) => name);
    const defaultGroupIndex =
      stateDefaultTabIndex ?? tabList.findIndex(name => name === defaultGroup);
    const defaultTabIndex = defaultGroupIndex === -1 ? 0 : defaultGroupIndex;
    const tabIndex = stateSelectedTabIndex ?? defaultTabIndex;

    const totalsRow = {
      cells: [
        { text: totals.name },
        { text: formatCurrency(totals.endingMarketValue) },
        ...totals.performances.map(({ value }) => ({
          text: value === null ? '–' : formatPercentage(value / 100),
        })),
      ],
      isTotal: true,
    };

    tableData.headers = [
      {
        text: 'Holding',
      },
      {
        text: 'Market Value',
      },
      ...periodLabels.map(label => ({
        text: label,
      })),
    ];

    tableData.rows = [
      ...(isEmbeddedMobile ? [totalsRow] : []),

      ...groups[tabIndex].rows.map(
        ({
          id,
          name,
          endingMarketValue,
          performances,
          assetDetails,
          benchmarks: _benchmarks,
          color,
        }) => ({
          id,
          cells: [
            { text: name },
            { text: formatCurrency(endingMarketValue) },
            ...performances.map(({ value }) => ({
              text: value === null ? '–' : formatPercentage(value / 100),
            })),
          ],

          color,

          childRows: [
            ...assetDetails.map(childRow => ({
              cells: [
                { text: childRow.name },
                { text: formatCurrency(childRow.endingMarketValue) },
                ...childRow.performances.map(({ value }) => ({
                  text: value === null ? '–' : formatPercentage(value / 100),
                })),
              ],
            })),

            ..._benchmarks.map(benchmark => ({
              cells: [
                { text: benchmark.name },
                { text: '' },
                ...benchmark.performances.map(({ value }) => ({
                  text: value === null ? '–' : formatPercentage(value / 100),
                })),
              ],
              isBenchmark: true,
            })),
          ],
        }),
      ),

      ...(!isEmbeddedMobile ? [totalsRow] : []),

      ...benchmarks.map(benchmark => ({
        cells: [
          { text: benchmark.name },
          { text: '' },
          ...benchmark.performances.map(({ value }) => ({
            text: value === null ? '–' : formatPercentage(value / 100),
          })),
        ],
        isBenchmark: true,
      })),
    ];

    if (tabList.length > 0) {
      dispatch(setGroupSelectionTabsList(tabList, TabSlug.Performance));
    }

    if (!stateDefaultTabIndex) {
      dispatch(setGroupSelectionDefaultTabIndex(defaultGroupIndex, TabSlug.Performance));
    }

    return {
      tableData,
      tabList,
      defaultTabIndex,
    };
  }, [
    queryInfo.data,
    stateTabList,
    stateDefaultTabIndex,
    stateSelectedTabIndex,
    isEmbeddedMobile,
    dispatch,
  ]);

  return {
    ...queryInfo,
    data,
  };
};
